import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Plus, Loader2, Search, MapPin, ScanFace, Camera, Pencil, UserX, IndianRupee,
  Building2, Bike, Users, Clock, Navigation, ImagePlus, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import FaceEnrollDialog from './FaceEnrollDialog';

const DAYS = [
  { v: 'monday', l: 'Mon' }, { v: 'tuesday', l: 'Tue' }, { v: 'wednesday', l: 'Wed' },
  { v: 'thursday', l: 'Thu' }, { v: 'friday', l: 'Fri' }, { v: 'saturday', l: 'Sat' }, { v: 'sunday', l: 'Sun' },
];

const emptyForm = {
  name: '', phone: '', email: '', employee_type: 'office', designation: '', department: '',
  monthly_salary: '', date_of_joining: '', shift_start: '09:30', shift_end: '18:30',
  weekly_offs: ['sunday'], late_grace_minutes: 15, half_day_hours: 4, full_day_hours: 8,
  gps_required: false, geofence_enabled: false, site_name: '', site_lat: '', site_lng: '', geofence_radius_m: 200,
};

function EmployeeAvatar({ emp, API }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke;
    if (emp.has_photo) {
      axios.get(`${API}/hr/employees/${emp.employee_id}/photo`, { responseType: 'blob' })
        .then((res) => { const u = URL.createObjectURL(res.data); revoke = u; setUrl(u); })
        .catch(() => {});
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [emp.employee_id, emp.has_photo, API]);
  const initials = (emp.name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  if (url) return <img src={url} alt={emp.name} className="h-9 w-9 rounded-full object-cover" />;
  return <div className="h-9 w-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-semibold">{initials}</div>;
}

export default function EmployeeDirectory() {
  const { API } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ total: 0, office: 0, field: 0, face_enrolled: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [faceTarget, setFaceTarget] = useState(null);
  const [geoLocating, setGeoLocating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.employee_type = typeFilter;
      if (search) params.search = search;
      const [list, st] = await Promise.all([
        axios.get(`${API}/hr/employees`, { params }),
        axios.get(`${API}/hr/stats`),
      ]);
      setEmployees(list.data.employees || []);
      setStats(st.data);
    } catch (e) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [API, typeFilter, search]);

  useEffect(() => {
    const t = setTimeout(fetchData, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchData, search]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      ...emptyForm, ...emp,
      monthly_salary: emp.monthly_salary ?? '',
      site_lat: emp.site_lat ?? '', site_lng: emp.site_lng ?? '',
      date_of_joining: emp.date_of_joining ? emp.date_of_joining.slice(0, 10) : '',
    });
    setDialogOpen(true);
  };

  const toggleDay = (d) => {
    setForm((f) => ({ ...f, weekly_offs: f.weekly_offs.includes(d) ? f.weekly_offs.filter((x) => x !== d) : [...f.weekly_offs, d] }));
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setGeoLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, site_lat: pos.coords.latitude.toFixed(6), site_lng: pos.coords.longitude.toFixed(6) })); setGeoLocating(false); toast.success('Location captured'); },
      () => { setGeoLocating(false); toast.error('Could not get location (allow permission)'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error('Name and phone are required'); return; }
    if (form.monthly_salary === '' || isNaN(Number(form.monthly_salary))) { toast.error('Enter a valid salary'); return; }
    if (form.geofence_enabled && (form.site_lat === '' || form.site_lng === '')) { toast.error('Set the site location for geo-fence'); return; }
    setSaving(true);
    const payload = {
      ...form,
      monthly_salary: Number(form.monthly_salary),
      late_grace_minutes: Number(form.late_grace_minutes),
      half_day_hours: Number(form.half_day_hours),
      full_day_hours: Number(form.full_day_hours),
      geofence_radius_m: Number(form.geofence_radius_m),
      site_lat: form.site_lat === '' ? null : Number(form.site_lat),
      site_lng: form.site_lng === '' ? null : Number(form.site_lng),
      date_of_joining: form.date_of_joining || null,
    };
    try {
      if (editing) await axios.put(`${API}/hr/employees/${editing.employee_id}`, payload);
      else await axios.post(`${API}/hr/employees`, payload);
      toast.success(editing ? 'Employee updated' : 'Employee added');
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (emp) => {
    if (!window.confirm(`Deactivate ${emp.name}?`)) return;
    try { await axios.delete(`${API}/hr/employees/${emp.employee_id}`); toast.success('Employee deactivated'); fetchData(); }
    catch { toast.error('Failed to deactivate'); }
  };

  const uploadPhoto = async (emp, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try { await axios.post(`${API}/hr/employees/${emp.employee_id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Photo updated'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Photo upload failed'); }
  };

  const handleEnroll = async (descriptors, selfie) => {
    try {
      await axios.post(`${API}/hr/employees/${faceTarget.employee_id}/face-enroll`, { descriptors, selfie });
      toast.success(`Face enrolled for ${faceTarget.name}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Face enrollment failed'); }
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card><CardContent className="p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}><Icon className="h-5 w-5" /></div>
      <div><div className="text-2xl font-bold">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
    </CardContent></Card>
  );

  return (
    <div className="space-y-5" data-testid="hr-employee-directory">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Employees</h1>
          <p className="text-sm text-slate-500">Manage staff, shifts, GPS rules and face enrollment</p>
        </div>
        <Button onClick={openAdd} className="bg-slate-800 hover:bg-slate-900" data-testid="add-employee-btn"><Plus className="h-4 w-4 mr-1" /> Add Employee</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total Staff" value={stats.total} color="bg-slate-100 text-slate-700" />
        <StatCard icon={Building2} label="Office" value={stats.office} color="bg-blue-100 text-blue-700" />
        <StatCard icon={Bike} label="Field" value={stats.field} color="bg-amber-100 text-amber-700" />
        <StatCard icon={ScanFace} label="Face Enrolled" value={stats.face_enrolled} color="bg-emerald-100 text-emerald-700" />
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search name, phone or code" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="employee-search" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40" data-testid="type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="field">Field</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : employees.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">No employees yet. Click "Add Employee" to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Salary</TableHead>
                    <TableHead>GPS</TableHead>
                    <TableHead>Face</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp.employee_id} data-testid={`employee-row-${emp.employee_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <EmployeeAvatar emp={emp} API={API} />
                          <div>
                            <div className="font-medium text-slate-800">{emp.name}</div>
                            <div className="text-xs text-slate-400">{emp.emp_code} · {emp.phone}{emp.designation ? ` · ${emp.designation}` : ''}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {emp.employee_type === 'office'
                          ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Building2 className="h-3 w-3 mr-1" />Office</Badge>
                          : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Bike className="h-3 w-3 mr-1" />Field</Badge>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600"><Clock className="h-3 w-3 inline mr-1 text-slate-400" />{emp.shift_start}–{emp.shift_end}</TableCell>
                      <TableCell className="text-sm font-medium text-slate-700">₹{Number(emp.monthly_salary).toLocaleString('en-IN')}</TableCell>
                      <TableCell>
                        {emp.geofence_enabled
                          ? <Badge variant="outline" className="text-emerald-700 border-emerald-200"><MapPin className="h-3 w-3 mr-1" />{emp.geofence_radius_m}m</Badge>
                          : emp.gps_required
                            ? <Badge variant="outline" className="text-slate-600"><Navigation className="h-3 w-3 mr-1" />Capture</Badge>
                            : <span className="text-xs text-slate-300">—</span>}
                      </TableCell>
                      <TableCell>
                        {emp.face_enrolled
                          ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" />Enrolled</Badge>
                          : <Badge variant="outline" className="text-slate-400">Not set</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Enroll face" onClick={() => setFaceTarget(emp)} data-testid={`face-enroll-${emp.employee_id}`}><ScanFace className="h-4 w-4 text-emerald-600" /></Button>
                          <label title="Upload photo">
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(emp, e.target.files?.[0])} />
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-100 cursor-pointer"><ImagePlus className="h-4 w-4 text-slate-500" /></span>
                          </label>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => openEdit(emp)} data-testid={`edit-employee-${emp.employee_id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Deactivate" onClick={() => deactivate(emp)} data-testid={`deactivate-${emp.employee_id}`}><UserX className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            <DialogDescription>Staff details, shift timing and attendance rules.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" /></div>
              <div><Label>Phone (for login) *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div>
                <Label>Employee Type *</Label>
                <Select value={form.employee_type} onValueChange={(v) => setForm({ ...form, employee_type: v, gps_required: v === 'field' ? true : form.gps_required })}>
                  <SelectTrigger data-testid="emp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">Office (face check-in)</SelectItem>
                    <SelectItem value="field">Field (GPS check-in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
              <div><Label>Monthly Salary (₹) *</Label><Input type="number" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} data-testid="emp-salary" /></div>
              <div><Label>Date of Joining</Label><Input type="date" value={form.date_of_joining} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} /></div>
            </div>

            {/* Shift */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><Clock className="h-4 w-4" /> Shift & Attendance</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><Label>Shift Start</Label><Input type="time" value={form.shift_start} onChange={(e) => setForm({ ...form, shift_start: e.target.value })} /></div>
                <div><Label>Shift End</Label><Input type="time" value={form.shift_end} onChange={(e) => setForm({ ...form, shift_end: e.target.value })} /></div>
                <div><Label>Late Grace (min)</Label><Input type="number" value={form.late_grace_minutes} onChange={(e) => setForm({ ...form, late_grace_minutes: e.target.value })} /></div>
                <div><Label>Full-day Hours</Label><Input type="number" value={form.full_day_hours} onChange={(e) => setForm({ ...form, full_day_hours: e.target.value })} /></div>
              </div>
              <div className="mt-3">
                <Label className="text-xs text-slate-500">Weekly Offs</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {DAYS.map((d) => (
                    <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${form.weekly_offs.includes(d.v) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}
                      data-testid={`weekoff-${d.v}`}>{d.l}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* GPS */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><MapPin className="h-4 w-4" /> Location / GPS Rule</h4>
              <div className="flex items-center justify-between py-1">
                <Label className="text-sm">Capture GPS at check-in</Label>
                <Switch checked={form.gps_required} onCheckedChange={(v) => setForm({ ...form, gps_required: v })} data-testid="gps-required-switch" />
              </div>
              <div className="flex items-center justify-between py-1">
                <Label className="text-sm">Restrict to a site (geo-fence)</Label>
                <Switch checked={form.geofence_enabled} onCheckedChange={(v) => setForm({ ...form, geofence_enabled: v, gps_required: v ? true : form.gps_required })} data-testid="geofence-switch" />
              </div>
              {form.geofence_enabled && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                  <div className="col-span-2 sm:col-span-1"><Label>Site Name</Label><Input value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} /></div>
                  <div><Label>Latitude</Label><Input value={form.site_lat} onChange={(e) => setForm({ ...form, site_lat: e.target.value })} data-testid="site-lat" /></div>
                  <div><Label>Longitude</Label><Input value={form.site_lng} onChange={(e) => setForm({ ...form, site_lng: e.target.value })} data-testid="site-lng" /></div>
                  <div><Label>Radius (m)</Label><Input type="number" value={form.geofence_radius_m} onChange={(e) => setForm({ ...form, geofence_radius_m: e.target.value })} /></div>
                  <div className="col-span-2 sm:col-span-4">
                    <Button type="button" size="sm" variant="outline" onClick={useCurrentLocation} disabled={geoLocating} data-testid="use-location-btn">
                      {geoLocating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Navigation className="h-4 w-4 mr-1" />} Use my current location
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-800 hover:bg-slate-900" data-testid="save-employee-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <IndianRupee className="h-4 w-4 mr-1" />} {editing ? 'Update' : 'Add'} Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FaceEnrollDialog open={!!faceTarget} onOpenChange={(v) => !v && setFaceTarget(null)} employee={faceTarget} onEnroll={handleEnroll} />
    </div>
  );
}
